import { FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useContactmomentMutaties } from '../api/contactmomenten.ts';
import type {
  Contactkanaal,
  Contactmoment,
  Contactpersoon,
  Contactrichting,
  Kandidaat,
  Vacature,
} from '../api/typen.ts';
import { volledigeNaam } from '../helpers/formatteer.ts';
import { naarLokaalTijdstip, nuLokaal, vanLokaalTijdstip } from '../helpers/formulier.ts';
import { contactkanaalLabels, contactrichtingLabels, opties } from '../helpers/labels.ts';
import { useVerzenden } from '../hooks/useVerzenden.ts';
import { FormulierDialoog } from './FormulierDialoog.tsx';
import { ContactpersoonVeld, KandidaatVeld, VacatureVeld } from './keuzevelden.tsx';
import { Keuzeveld, Tekstveld } from './velden.tsx';

interface Waarden {
  metWie: 'kandidaat' | 'contactpersoon';
  kandidaat: Kandidaat | null;
  contactpersoon: Contactpersoon | null;
  vacature: Vacature | null;
  kanaal: Contactkanaal | '';
  richting: Contactrichting | '';
  tijdstip: string;
  notitie: string;
}

interface Props {
  open: boolean;
  onSluit: () => void;
  /** De kandidaat waarmee het gesprek was, als die vaststaat. */
  kandidaat?: Kandidaat | undefined;
  /** De contactpersoon waarmee het gesprek was, als die vaststaat. */
  contactpersoon?: Contactpersoon | undefined;
  /** Het bedrijf waarvan de contactpersoon gekozen wordt, of waarover het gesprek met een kandidaat ging. */
  bedrijfId?: string | null | undefined;
  /** De vacature waarover het gesprek ging, als die vaststaat. */
  vacature?: Vacature | undefined;
  /** Een bestaand gesprek om te wijzigen; dan zijn alleen kanaal, richting, tijdstip en notitie te wijzigen. */
  bestaand?: Contactmoment | undefined;
}

const kanaalOpties = opties(contactkanaalLabels);
const richtingOpties = opties(contactrichtingLabels);

/**
 * Legt een gesprek vast met een kandidaat of een contactpersoon, of wijzigt een bestaand gesprek.
 * @param props Met wie het gesprek was, waarover, en of het om een bestaand gesprek gaat.
 * @returns De dialoog.
 */
export function ContactmomentDialoog({
  open,
  onSluit,
  kandidaat,
  contactpersoon,
  bedrijfId,
  vacature,
  bestaand,
}: Props) {
  const mutaties = useContactmomentMutaties();
  const standaardMetWie: Waarden['metWie'] =
    contactpersoon || (bedrijfId && !kandidaat) ? 'contactpersoon' : 'kandidaat';
  const formulier = useForm<Waarden>({
    defaultValues: {
      metWie: standaardMetWie,
      kandidaat: kandidaat ?? null,
      contactpersoon: contactpersoon ?? null,
      vacature: vacature ?? null,
      kanaal: 'telefoon',
      richting: 'uitgaand',
      tijdstip: nuLokaal(),
      notitie: '',
    },
  });

  useEffect(() => {
    if (open) {
      formulier.reset({
        metWie: standaardMetWie,
        kandidaat: kandidaat ?? null,
        contactpersoon: contactpersoon ?? null,
        vacature: vacature ?? null,
        kanaal: bestaand?.kanaal ?? 'telefoon',
        richting: bestaand?.richting ?? 'uitgaand',
        tijdstip: bestaand ? naarLokaalTijdstip(bestaand.tijdstip) : nuLokaal(),
        notitie: bestaand?.notitie ?? '',
      });
    }
  }, [open, kandidaat, contactpersoon, vacature, bestaand, formulier, standaardMetWie]);

  const metWie = formulier.watch('metWie');

  const { onVerzend, fout, bezig } = useVerzenden(
    formulier,
    async (waarden) => {
      const tijdstip = vanLokaalTijdstip(waarden.tijdstip);
      if (bestaand) {
        await mutaties.wijzig.mutateAsync({
          id: bestaand.id,
          invoer: {
            ...(waarden.kanaal ? { kanaal: waarden.kanaal } : {}),
            ...(waarden.richting ? { richting: waarden.richting } : {}),
            ...(tijdstip ? { tijdstip } : {}),
            notitie: waarden.notitie || null,
          },
        });
        return;
      }
      if (!waarden.kanaal || !waarden.richting) {
        throw new Error('Kies een kanaal en een richting.');
      }
      const metKandidaat = waarden.metWie === 'kandidaat';
      await mutaties.legVast.mutateAsync({
        kanaal: waarden.kanaal,
        richting: waarden.richting,
        ...(tijdstip ? { tijdstip } : {}),
        notitie: waarden.notitie || null,
        kandidaatId: metKandidaat ? (waarden.kandidaat?.id ?? null) : null,
        contactpersoonId: metKandidaat ? null : (waarden.contactpersoon?.id ?? null),
        bedrijfId: metKandidaat ? (bedrijfId ?? null) : null,
        vacatureId: waarden.vacature?.id ?? null,
      });
    },
    { melding: bestaand ? 'Gesprek gewijzigd' : 'Gesprek vastgelegd', naSucces: onSluit },
  );

  const vasteNaam = bestaand
    ? null
    : kandidaat
      ? `Met kandidaat ${volledigeNaam(kandidaat)}`
      : contactpersoon
        ? `Met contactpersoon ${volledigeNaam(contactpersoon)}`
        : null;

  return (
    <FormProvider {...formulier}>
      <FormulierDialoog
        open={open}
        titel={bestaand ? 'Gesprek wijzigen' : 'Gesprek vastleggen'}
        onSluit={onSluit}
        onVerzend={onVerzend}
        fout={fout}
        bezig={bezig}
        verzendLabel={bestaand ? 'Opslaan' : 'Vastleggen'}
      >
        <Stack spacing={2}>
          {vasteNaam && <Typography>{vasteNaam}</Typography>}
          {!bestaand && !kandidaat && !contactpersoon && (
            <>
              <RadioGroup
                row
                value={metWie}
                onChange={(gebeurtenis) => formulier.setValue('metWie', gebeurtenis.target.value as Waarden['metWie'])}
              >
                <FormControlLabel value="kandidaat" control={<Radio />} label="Met een kandidaat" />
                <FormControlLabel value="contactpersoon" control={<Radio />} label="Met een contactpersoon" />
              </RadioGroup>
              {metWie === 'kandidaat' ? (
                <KandidaatVeld naam="kandidaat" verplicht autoFocus />
              ) : (
                <ContactpersoonVeld naam="contactpersoon" bedrijfId={bedrijfId} verplicht autoFocus />
              )}
            </>
          )}
          {!bestaand && !vacature && <VacatureVeld naam="vacature" label="Over vacature (optioneel)" alleStatussen />}
          {vacature && !bestaand && <Typography color="text.secondary">Over vacature {vacature.titel}</Typography>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Keuzeveld naam="kanaal" label="Kanaal" opties={kanaalOpties} verplicht />
            <Keuzeveld naam="richting" label="Richting" opties={richtingOpties} verplicht />
          </Stack>
          <Tekstveld naam="tijdstip" label="Tijdstip" type="datetime-local" verplicht />
          <Tekstveld naam="notitie" label="Notitie" meerregelig rijen={4} />
        </Stack>
      </FormulierDialoog>
    </FormProvider>
  );
}
